// ---------------------------------------------------------------------------
// HRIS · Staff page
// ---------------------------------------------------------------------------
// Total-staff KPI tiles + a searchable / filterable / paginated datatable, with
// an "Add Staff" form (modal) that POSTs to /hris/staff. Rows can be edited or
// deleted inline. Backed entirely by HrisStaffService.
// ---------------------------------------------------------------------------
import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {FiChevronLeft, FiChevronRight, FiRefreshCw, FiSearch} from "react-icons/fi";
import {MdGroups, MdHowToReg, MdOutlinePersonOff, MdPersonAdd} from "react-icons/md";

import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import CardHeader from "../../../../../components/Card/CardHeader.js";
import IconBox from "../../../../../components/Icons/IconBox";
import AddStaffModal from "../../components/AddStaffModal";
import StaffTableRow from "./StaffTableRow";
import {STAFF_STATUS, STAFF_STATUS_META, STAFF_STATUS_ORDER} from "../../constants/HrisConstants";
import {deleteStaff, searchStaff} from "../../services/HrisStaffService";

const CAPTIONS = ["SN", "Staff", "Designation", "Contact", "Joined", "Status", "Actions"];

// Spring's Page wraps rows in `content`; fall back defensively for other shapes.
const getRows = (data) => data?.content || data?.staffList || data?.items || [];
const getTotalElements = (data, rows) => data?.totalElements ?? data?.totalItems ?? rows.length;
const getTotalPages = (data, total, size) => data?.totalPages ?? Math.max(1, Math.ceil(total / size));

const StatTile = ({title, value, icon, color}) => {
    const textColor = useColorModeValue("gray.700", "white");
    const subColor = useColorModeValue("gray.400", "gray.400");
    return (
        <Card minH="88px" p="16px">
            <Flex justify="space-between" align="center" h="100%">
                <Flex direction="column">
                    <Text fontSize="sm" color={subColor} fontWeight="bold" mb="2px">
                        {title}
                    </Text>
                    <Text fontSize="2xl" color={textColor} fontWeight="bold">
                        {value}
                    </Text>
                </Flex>
                <IconBox as="box" h="45px" w="45px" bg={color}>
                    {icon}
                </IconBox>
            </Flex>
        </Card>
    );
};

function Staff() {
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");

    // Datatable state
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters — `keyword` is what the user is typing; `appliedKeyword` is what's
    // actually sent to the server (only on submit), so we don't fire a request
    // per keystroke. Status applies immediately on change.
    const [keyword, setKeyword] = useState("");
    const [appliedKeyword, setAppliedKeyword] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // KPI totals (fetched independently of the current page/filters)
    const [totals, setTotals] = useState({all: 0, active: 0, onLeave: 0, inactive: 0});

    // Add / edit modal
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [editing, setEditing] = useState(null);

    // Delete confirmation
    const {isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose} = useDisclosure();
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const cancelRef = useRef();

    const loadStaff = useCallback(async () => {
        setLoading(true);
        try {
            const response = await searchStaff({keyword: appliedKeyword, status: statusFilter}, page, size);
            const list = getRows(response?.data);
            const total = getTotalElements(response?.data, list);
            setRows(list);
            setTotalElements(total);
            setTotalPages(getTotalPages(response?.data, total, size));
        } catch (error) {
            setRows([]);
            setTotalElements(0);
            setTotalPages(1);
            toast({
                title: "Unable to load staff",
                description: error?.errorMessage || error?.message || "Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [appliedKeyword, statusFilter, page, size, toast]);

    // Accurate KPI counts via lightweight size=1 queries per status. Each is
    // resilient: a failing status (e.g. enum mismatch) just shows 0.
    const loadTotals = useCallback(async () => {
        const countFor = async (status) => {
            try {
                const res = await searchStaff({status}, 0, 1);
                return getTotalElements(res?.data, getRows(res?.data));
            } catch (e) {
                return 0;
            }
        };
        const [all, active, onLeave, inactive] = await Promise.all([
            countFor(undefined),
            countFor(STAFF_STATUS.ACTIVE),
            countFor(STAFF_STATUS.ON_LEAVE),
            countFor(STAFF_STATUS.INACTIVE),
        ]);
        setTotals({all, active, onLeave, inactive});
    }, []);

    useEffect(() => {
        loadStaff();
    }, [loadStaff]);

    useEffect(() => {
        loadTotals();
    }, [loadTotals]);

    const refreshAll = () => {
        loadStaff();
        loadTotals();
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        setAppliedKeyword(keyword); // loadStaff re-runs via its dependency change
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setPage(0);
    };

    const openAdd = () => {
        setEditing(null);
        onOpen();
    };

    const openEdit = (staff) => {
        setEditing(staff);
        onOpen();
    };

    const askDelete = (staff) => {
        setDeleteTarget(staff);
        onDeleteOpen();
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteStaff(deleteTarget.id);
            toast({
                title: "Staff removed",
                description: `${deleteTarget.fullName} was deleted.`,
                status: "success",
                duration: 4000,
                isClosable: true,
            });
            onDeleteClose();
            setDeleteTarget(null);
            refreshAll();
        } catch (error) {
            toast({
                title: "Could not delete staff",
                description: error?.errorMessage || error?.message || "Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Flex direction="column" pt={{base: "120px", md: "75px"}} gap="20px">
            {/* Header */}
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}}
                  direction={{base: "column", md: "row"}} gap="12px">
                <Box>
                    <Heading as="h1" size="lg" color={textColor}>Staff</Heading>
                    <Text color={mutedColor} mt="4px">
                        Manage your school's staff records.
                    </Text>
                </Box>
                <HStack>
                    <Button leftIcon={<FiRefreshCw/>} variant="outline" onClick={refreshAll} isLoading={loading}>
                        Refresh
                    </Button>
                    <Button leftIcon={<MdPersonAdd/>} colorScheme="teal" onClick={openAdd}>
                        Add Staff
                    </Button>
                </HStack>
            </Flex>

            {/* KPI tiles */}
            <SimpleGrid columns={{base: 1, md: 2, xl: 4}} spacing="20px">
                <StatTile title="Total Staff" value={totals.all} color="teal.300"
                          icon={<MdGroups size="24px" color="white"/>}/>
                <StatTile title="Active" value={totals.active} color="green.400"
                          icon={<MdHowToReg size="24px" color="white"/>}/>
                <StatTile title="On Leave" value={totals.onLeave} color="orange.400"
                          icon={<MdPersonAdd size="24px" color="white"/>}/>
                <StatTile title="Inactive" value={totals.inactive} color="gray.400"
                          icon={<MdOutlinePersonOff size="24px" color="white"/>}/>
            </SimpleGrid>

            {/* Datatable */}
            <Card overflowX={{sm: "scroll", xl: "hidden"}}>
                <CardHeader p="16px 0 16px 0">
                    <Flex direction="column" w="100%" gap="16px">
                        <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
                            <HStack>
                                <Text fontSize="lg" color={textColor} fontWeight="bold">
                                    Staff Directory
                                </Text>
                                <Badge colorScheme="teal" borderRadius="6px">
                                    {totalElements} total
                                </Badge>
                            </HStack>
                        </Flex>

                        <Flex as="form" onSubmit={handleSearch} gap="12px" wrap="wrap" align="center">
                            <InputGroup maxW="300px">
                                <InputLeftElement pointerEvents="none" children={<FiSearch color="gray"/>}/>
                                <Input
                                    placeholder="Search name, code, email…"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </InputGroup>
                            <Select maxW="190px" placeholder="All statuses" value={statusFilter}
                                    onChange={handleStatusChange}>
                                {STAFF_STATUS_ORDER.map((s) => (
                                    <option key={s} value={s}>{STAFF_STATUS_META[s].label}</option>
                                ))}
                            </Select>
                            <Button type="submit" colorScheme="teal" leftIcon={<FiSearch/>} isLoading={loading}>
                                Search
                            </Button>
                            <Select size="sm" maxW="130px" value={size} ml="auto"
                                    onChange={(e) => {
                                        setPage(0);
                                        setSize(Number(e.target.value));
                                    }}>
                                {[10, 20, 50, 100].map((v) => <option key={v} value={v}>{v} / page</option>)}
                            </Select>
                        </Flex>
                    </Flex>
                </CardHeader>

                <CardBody>
                    {loading ? (
                        <Flex justify="center" align="center" py="50px" w="100%">
                            <Spinner size="lg" color="teal.400" thickness="3px"/>
                        </Flex>
                    ) : (
                        <Box overflowX="auto" w="100%">
                            <Table variant="simple" color={textColor}>
                                <Thead>
                                    <Tr my=".8rem" pl="0px" color="gray.400">
                                        {CAPTIONS.map((c, idx) => (
                                            <Th color="gray.400" key={idx} ps={idx === 0 ? "0px" : null}>
                                                {c}
                                            </Th>
                                        ))}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rows.map((staff, index) => (
                                        <StaffTableRow
                                            key={staff.id ?? `${page}-${index}`}
                                            sn={page * size + index + 1}
                                            staff={staff}
                                            onEdit={openEdit}
                                            onDelete={askDelete}
                                        />
                                    ))}
                                </Tbody>
                            </Table>
                            {rows.length === 0 ? (
                                <Flex direction="column" align="center" py="40px" w="100%">
                                    <Heading size="sm" color={textColor}>No staff found</Heading>
                                    <Text color={mutedColor} mt="6px">
                                        Add your first staff member to get started.
                                    </Text>
                                </Flex>
                            ) : null}
                        </Box>
                    )}
                </CardBody>

                {/* Pagination */}
                <Flex justify="space-between" align="center" px="22px" pb="22px" gap="12px" wrap="wrap">
                    <Text color={mutedColor} fontSize="sm">Page {page + 1} of {totalPages}</Text>
                    <HStack>
                        <Button leftIcon={<FiChevronLeft/>} size="sm"
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                isDisabled={loading || page === 0}>
                            Previous
                        </Button>
                        <Button rightIcon={<FiChevronRight/>} size="sm"
                                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                isDisabled={loading || page + 1 >= totalPages}>
                            Next
                        </Button>
                    </HStack>
                </Flex>
            </Card>

            {/* Add / Edit modal */}
            <AddStaffModal
                isOpen={isOpen}
                onClose={onClose}
                staff={editing}
                onSaved={refreshAll}
            />

            {/* Delete confirmation */}
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Staff</AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete{" "}
                            <b>{deleteTarget?.fullName}</b>? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose} isDisabled={deleting}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={confirmDelete} ml={3} isLoading={deleting}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
}

export default Staff;
